import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { ModalProps, Spinner } from 'react-bootstrap';
import Select, { StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addMembers } from '../../features/setting/settingSlice';
import { ModalBox, RoundedAvatar } from '../../components';
import { ModalName } from '../../types/modal';
import { ClassTeam } from '../../pages/tourDetails/TournamentClasses/components/ClassesTag/ClassesTag';
import { fetchUsers } from '../../features/users/actionCreators';
import { clearUsers, addUsersFromSearch } from '../../features/users/userSlice';
import { fetchUsersData } from '../../apis/tournament';
import { Team, User } from '../../types/tournament';
import { imgOptionUserLabel } from '../../utils/imgOptionUserLabel';
import { OrganisationData } from '../../types/adminOrganisation';
import { ClassTypeEnum, TypeError } from '../../types/enums';
import { generateTeamName } from '../../utils/generateTeamName';
import { rosterAfterNewCaptain } from '../../utils/teamRosterForm';
import { getDisplayName } from '../../utils/userDisplayName';
import { Notification } from '../../hooks/useNotification';

type Option = { value: number; label: string };

export const customStyles: StylesConfig = {
  control: (base) => ({ ...base, borderRadius: '16px', minHeight: '50px' }),
};

interface MemberModalProps extends ModalProps {
  selected?: User[];
}

export const MemberModal = ({ selected = [], ...props }: MemberModalProps) => {
  const { data } = useAppSelector((state) => state.modals);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { userData, isLoading } = useAppSelector((state) => state.users);
  const [multiSelections, setMultiSelections] = useState<User[]>([]);

  const prefix = 'modal-';
  const inputPrefix = 'input-';

  useMemo(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleSelect = useCallback(
    (e: Option[]) => {
      setMultiSelections(
        //@ts-ignore
        e.map((option) => userData.find((user) => user.id === option.value))
      );
    },
    [userData]
  );

  const excludedRosterUserIds = useMemo(() => {
    if (data?.modalName !== ModalName.ADD_TEAM_PLAYERS_CLASS) {
      return new Set<number>();
    }
    const currentTeam = (data.teams as Team[] | undefined)?.find(
      (team: Team) => team.id === data.teamId
    );
    return new Set((currentTeam?.users ?? []).map((u) => (typeof u === 'number' ? u : u.id)));
  }, [data]);

  const promiseOptions = useMemo(
    () =>
      debounce((inputValue, cb) => {
        fetchUsersData(inputValue)
          .then((response) => {
            dispatch(addUsersFromSearch(response));
            const filtered = response.filter((user: User) => !excludedRosterUserIds.has(user.id));
            cb(filtered.map((user: User) => imgOptionUserLabel(user)));
          })
          .catch((e) => {
            console.log(e);
          });
      }, 500),
    [dispatch, excludedRosterUserIds]
  );

  const selectDefaultOptions = useMemo(
    () =>
      userData
        .filter((user) => !excludedRosterUserIds.has(user.id))
        .map((user) => imgOptionUserLabel(user)),
    [userData, excludedRosterUserIds]
  );

  const handleSave = useCallback(() => {
    if (data?.modalName === ModalName.ADD_TOURNAMENT_MEMBER) {
      const newMulti = multiSelections.filter(
        (nm) => !((data.selected as User[]) || []).find((om) => om.id === nm.id)
      );
      dispatch(addMembers([...data.selected, ...newMulti]));
    } else if (data?.modalName === ModalName.ADD_TEAM_PLAYERS_CLASS) {
      const { teams, setField, teamId, type } = data;
      const currentTeam = teams.find((team: Team) => team.id === teamId);

      let usersRaw;

      if (type?.value === ClassTypeEnum.SINGLES && multiSelections.length >= 1) {
        usersRaw = multiSelections.slice(0, 1);
      } else if (type?.value === ClassTypeEnum.DOUBLES && multiSelections.length >= 2) {
        usersRaw = multiSelections.slice(0, 2);
      } else if (type?.value === ClassTypeEnum.TEAMS) {
        const maxRoster = Number(data.maxTeamRoster) || 4;
        const currentUsers = currentTeam.users ?? [];
        const remaining = Math.max(0, maxRoster - currentUsers.length);
        // Filter duplicates first so they do not consume remaining slot budget
        const uniqueNew = multiSelections.filter(
          (u) => !currentUsers.find((ctu: User) => ctu.id === u.id)
        );
        for (const user of uniqueNew) {
          const otherTeam = (teams as ClassTeam[]).find(
            (team: ClassTeam) =>
              team.id !== teamId &&
              (team.users ?? []).some((u: User) => u.id === user.id)
          );
          if (otherTeam) {
            Notification(
              TypeError.warning,
              t(prefix + 'alreadyOnOtherTeam', {
                player: getDisplayName(user) || String(user.id),
                team: otherTeam.name,
              })
            );
          }
        }
        usersRaw = uniqueNew.slice(0, remaining);
        if (usersRaw.length < uniqueNew.length) {
          Notification(TypeError.warning, t(prefix + 'rosterOverflowTruncated'));
        }
      } else {
        usersRaw = multiSelections;
      }

      let users = [] as User[];
      if (
        type?.value === ClassTypeEnum.DOUBLES &&
        usersRaw.length === 1 &&
        currentTeam.users.length === 1
      ) {
        users = [
          ...currentTeam.users,
          ...usersRaw.filter((u) => !currentTeam.users.find((ctu: User) => ctu.id === u.id)),
        ];
      } else if (type?.value === ClassTypeEnum.TEAMS) {
        const maxRoster = Number(data.maxTeamRoster) || 4;
        users = [...currentTeam.users, ...usersRaw].slice(0, maxRoster);
      } else {
        users = usersRaw;
      }

      setField(
        'teams',
        teams.map((team: ClassTeam) =>
          team.id === teamId
            ? {
                ...team,
                name: generateTeamName(users, type, team),
                users,
              }
            : team
        )
      );
    } else if (data?.modalName === ModalName.ADD_CREATIVES_MEMBER) {
      data.setOrganisationData((prevState: OrganisationData) => {
        const newMulti = multiSelections.filter(
          (nm) => !((prevState.creatives as User[]) || []).find((om) => om.id === nm.id)
        );

        return {
          ...prevState,
          creatives: [...prevState.creatives, ...newMulti],
        };
      });
    } else if (data?.modalName === ModalName.ADD_TEAM_CAPTAIN_CLASS) {
      const { teams, setField, teamId, type, rosterOnly } = data;

      const usersRaw = multiSelections.slice(0, 1);
      const captain = usersRaw;
      const captainUser = captain[0];

      if (type?.value === ClassTypeEnum.TEAMS) {
        const maxRoster = Number(data.maxTeamRoster) || 4;
        const currentTeam = (teams as ClassTeam[]).find((team) => team.id === teamId);
        const currentUsers = currentTeam?.users ?? [];
        const addingOffRoster =
          !rosterOnly &&
          captainUser != null &&
          !currentUsers.some((user: User) => user.id === captainUser.id);
        const nextRoster =
          addingOffRoster && captainUser
            ? rosterAfterNewCaptain({
                users: currentUsers,
                captain: captainUser,
                maxRoster,
              })
            : null;

        if (nextRoster?.overflow) {
          Notification(TypeError.warning, t(prefix + 'captainRosterOverflowTruncated'));
        } else {
          setField(
            'teams',
            teams.map((team: ClassTeam) => {
              if (team.id !== teamId) {
                return team;
              }

              return {
                ...team,
                captain,
                users: nextRoster?.users ?? team.users ?? [],
              };
            })
          );
        }
      }
    }

    dispatch(clearUsers());
    props.onHide && props.onHide();
  }, [data, dispatch, multiSelections, props, t]);

  const rosterOnly = Boolean(data?.rosterOnly);
  const rosterUsers: User[] = useMemo(() => {
    if (!rosterOnly || data?.modalName !== ModalName.ADD_TEAM_CAPTAIN_CLASS) {
      return [];
    }
    const currentTeam = (data.teams as ClassTeam[] | undefined)?.find(
      (team: ClassTeam) => team.id === data.teamId
    );
    return currentTeam?.users ?? [];
  }, [data, rosterOnly]);

  const handleRosterSelect = useCallback(
    (e: Option[] | null) => {
      const selected = e ?? [];
      setMultiSelections(
        selected
          .map((option) => rosterUsers.find((user) => user.id === option.value))
          .filter((user): user is User => Boolean(user))
      );
    },
    [rosterUsers]
  );

  const optionLabel = (option: any) => (
    <div className="d-flex align-items-center">
      <RoundedAvatar avatar={option.avatar} alt="User avatar" name="user" />
      {option.label}
    </div>
  );

  return (
    <ModalBox {...props} title={data?.title} handleSave={handleSave}>
      {rosterOnly ? (
        <Select
          styles={customStyles}
          isMulti
          name="members"
          className="basic-multi-select holder-select"
          classNamePrefix="select"
          options={rosterUsers.map((user) => imgOptionUserLabel(user))}
          placeholder={t(inputPrefix + 'placeholderSelect') + '...'}
          //@ts-ignore
          onChange={handleRosterSelect}
          formatOptionLabel={optionLabel}
        />
      ) : isLoading ? (
        <div className="d-flex justify-content-center align-items-center">
          <p className="mb-0 mx-3">{t(prefix + 'userFetch') + '...'}</p>
          <Spinner size="sm" animation="border" variant="dark" />
        </div>
      ) : (
        <AsyncSelect
          styles={customStyles}
          isMulti
          name="members"
          className="basic-multi-select holder-select"
          classNamePrefix="select"
          defaultOptions={selectDefaultOptions}
          loadOptions={promiseOptions}
          placeholder={t(inputPrefix + 'placeholderSelect') + '...'}
          //@ts-ignore
          onChange={handleSelect}
          formatOptionLabel={optionLabel}
        />
      )}
    </ModalBox>
  );
};
